import React, { useCallback, useEffect, useMemo, useState } from 'react';
import VideoCard from '../VideoCard/VideoCard';
import VideoCardSkeleton from '../VideoCard/VideoCardSkeleton';
import { searchVideos, VideoSearchFilters } from '../../services/video.service';
import { VideoResponse } from '../../types/video.types';
import { VideoCardData } from '../../types/video-card.types';
import { buildVideoCardData } from '../../utils/video-card';
import NoVideosFound from '../NoVideosFound/NoVideosFound';
import SearchForVideo from '../SearchForVideo/SearchForVideo';
import { Routes } from '../../types/constants';
import styles from './VideoList.module.scss';

export type VideoListViewMode = 'grid' | 'compact';

interface VideoListProps {
    pageSize: number;
    searchTerm: string;
    tags?: string[];
    viewMode?: VideoListViewMode;
    filters?: VideoSearchFilters;
    allowDefaultLoad?: boolean;
    centerNoResults?: boolean;
    emptyState?: React.ReactNode;
    watchContext?: {
        query?: string;
        tags?: string[];
    };
}

const VideoList = ({
    pageSize,
    searchTerm,
    tags,
    viewMode = 'grid',
    filters,
    allowDefaultLoad = false,
    centerNoResults = true,
    emptyState,
    watchContext
}: VideoListProps) => {
    const [videos, setVideos] = useState<VideoCardData[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [showNoVideos, setShowNoVideos] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const normalizedSearchTerm = searchTerm.trim();
    const activeTags = useMemo(
        () => tags?.filter((tag) => tag.trim().length > 0) ?? [],
        [tags]
    );
    const activeTagKey = useMemo(() => activeTags.join('|'), [activeTags]);
    const normalizedFilters = useMemo<VideoSearchFilters>(() => ({
        sort: filters?.sort ?? 'newest',
        event: filters?.event?.trim() ?? '',
        fighter: filters?.fighter?.trim() ?? '',
        venue: filters?.venue?.trim() ?? '',
        type: filters?.type?.trim() ?? '',
        year: filters?.year,
        decade: filters?.decade
    }), [filters?.sort, filters?.event, filters?.fighter, filters?.venue, filters?.type, filters?.year, filters?.decade]);
    const activeFilterKey = useMemo(() => JSON.stringify(normalizedFilters), [normalizedFilters]);
    const hasRichFilters = Object.entries(normalizedFilters).some(([key, value]) => (
        key !== 'sort' && value !== undefined && value !== ''
    ));
    const hasFilters = normalizedSearchTerm.length > 0 || activeTags.length > 0 || hasRichFilters || allowDefaultLoad;
    const isSearchResultContext = normalizedSearchTerm.length > 0 || activeTags.length > 0 || hasRichFilters;
    const watchQuery = watchContext?.query?.trim() ?? '';
    const watchTags = useMemo(
        () => watchContext?.tags?.filter((tag) => tag.trim().length > 0) ?? [],
        [watchContext?.tags]
    );
    const hasWatchContext = watchQuery.length > 0 || watchTags.length > 0;
    const isCompactView = viewMode === 'compact';

    useEffect(() => {
        if (videos.length === 0 && !isLoading && hasFilters) {
            const timer = setTimeout(() => {
                setShowNoVideos(true);
            }, 500); // 500 milliseconds delay
            return () => clearTimeout(timer);
        } else {
            setShowNoVideos(false);
        }
    }, [videos, isLoading, hasFilters]);

    // Reset state when searchTerm or pageSize changes
    useEffect(() => {
        setCurrentPage(1);
        setVideos([]);
        setHasMore(true);
        setErrorMessage(null);
    }, [searchTerm, pageSize, activeTagKey, activeFilterKey, allowDefaultLoad]);

    // Fetch videos
    const fetchVideos = useCallback(async () => {
        if (!hasFilters) {
            setIsLoading(false);
            setHasMore(false);
            return;
        }
        setIsLoading(true);
        setErrorMessage(null);
        try {
            const response: VideoResponse = await searchVideos(
                normalizedSearchTerm,
                currentPage,
                pageSize,
                activeTags,
                normalizedFilters
            );
            const newVideos = response.Results.map((video) => {
                const card = buildVideoCardData(video);
                if (!hasWatchContext) {
                    return card;
                }
                const params = new URLSearchParams();
                params.set('v', video.Url);
                if (watchQuery.length > 0) {
                    params.set('q', watchQuery);
                }
                watchTags.forEach((tag) => params.append('tags', tag));
                return {
                    ...card,
                    watchUrl: `${Routes.WATCH}?${params.toString()}`
                };
            });

            setVideos(prevVideos => {
                const videoIds = new Set(prevVideos.map(video => video.id));
                const uniqueNewVideos = newVideos.filter(video => !videoIds.has(video.id));
                return [...prevVideos, ...uniqueNewVideos]; // Append only new unique videos
            });

            if (newVideos.length === 0 || newVideos.length < pageSize) {
                setHasMore(false);
            }
        } catch (error) {
            setHasMore(false);
            setErrorMessage('Unable to load videos. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, [normalizedSearchTerm, currentPage, pageSize, hasFilters, activeTags, normalizedFilters, hasWatchContext, watchQuery, watchTags]);

    // Fetch videos when currentPage changes
    useEffect(() => {
        if (hasMore) {
            fetchVideos();
        }
    }, [currentPage, fetchVideos, hasMore]);

    const loadMore = () => {
        if (!isLoading && hasMore) {
            setCurrentPage(prevPage => prevPage + 1);
        }
    };

    const retry = () => {
        setErrorMessage(null);
        setVideos([]);
        if (currentPage !== 1) {
            setCurrentPage(1);
        }
        setHasMore(true);
    };

    const renderVideos = (): React.ReactNode => (
        <div className={`${styles.list} ${isCompactView ? styles.listCompact : ''}`}>
            <div className={`${styles.grid} ${isCompactView ? styles.gridCompact : ''}`}>
                {videos.map((video) => (
                    <div className={styles.cardShell} key={video.id}>
                        <VideoCard
                            video={video}
                            variant={isCompactView ? 'compact' : 'grid'}
                            analyticsContext={{
                                source: isSearchResultContext ? 'search_results' : 'video_list',
                                query: normalizedSearchTerm,
                                tags: activeTags
                            }}
                        />
                    </div>
                ))}
            </div>
            {hasMore && !isLoading && (
                <div className={styles.loadMoreWrap}>
                    <button
                        type="button"
                        onClick={loadMore}
                        className={styles.loadMoreButton}
                    >
                        Load More
                    </button>
                </div>
            )}
        </div>
    );

    return (
        <div>
            {
                !hasFilters ? (
                    <div className={`${styles.messageWrap} ${styles.messageWrapCentered}`}>
                        <SearchForVideo />
                    </div>
                ) : videos.length > 0 ? (
                    renderVideos()
                ) : errorMessage ? (
                    <div className={styles.messageWrap}>
                        <div className={styles.errorState} role="status">
                            <h3>Videos are not loading</h3>
                            <p>{errorMessage}</p>
                            <button type="button" className={styles.retryButton} onClick={retry}>
                                Retry
                            </button>
                        </div>
                    </div>
                ) : isLoading ? (
                    <div className={`${styles.grid} ${isCompactView ? styles.gridCompact : ''}`}>
                        {Array.from({ length: Math.min(pageSize, 6) }).map((_, index) => (
                            <div className={styles.cardShell} key={`video-skeleton-${index}`}>
                                <VideoCardSkeleton />
                            </div>
                        ))}
                    </div>
                ) : showNoVideos ? (
                    <div className={`${styles.messageWrap} ${centerNoResults ? styles.messageWrapCentered : ''}`}>
                        {emptyState ?? <NoVideosFound />}
                    </div>
                ) : null // Render nothing if still loading
            }
        </div>
    );
};

export default VideoList;
